import {
  buildDashboardDataTableColumns,
  DashboardDataTableColumnKind,
} from '@shigo-ui/dashboard-ui';
import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@shigo-ui/ui';
import { ResponsiveBarChart, ResponsiveDonutChart } from '@shigo-ui/viz';

import { dashboardT } from '../../../../lib/messages';
import type { dashboardCanvasRendererProps } from '@/components/dashboard/DashboardCanvas';
import { formatCurrency, formatNumber, formatPercent, formatRatio } from '../../../../lib/format';
import type { NetworkDemandSourceRow } from './model';
import { NetworkDashboardTable } from './NetworkDashboardTable';
import type { networkDashboardRuntime } from './widgetRuntime';
import { NetworkDashboardWidgetType, type NetworkDashboardWidget } from './widgetModel';

export function NetworkDemandRenderer({
  widget,
  runtime,
}: dashboardCanvasRendererProps<NetworkDashboardWidget, networkDashboardRuntime>) {
  const query = runtime.demandSources;
  const rows = query.data?.report ?? [];
  if (widget.type === NetworkDashboardWidgetType.DemandPerformance) {
    return (
      <section className="border-border bg-card h-full min-w-0 overflow-hidden rounded-md border shadow-[var(--shadow-xs)]">
        <header className="border-border border-b px-5 py-4">
          <h2 className="text-base font-semibold">
            {dashboardT('networkDashboard.demandPerformance')}
          </h2>
        </header>
        <NetworkDashboardTable
          title={dashboardT('networkDashboard.demandPerformance')}
          items={rows}
          columns={demandColumns()}
          getRowKey={(row) =>
            row.demandPartnerId ?? row.demandPartnerName ?? row.demandPartner ?? 'unknown'
          }
          sortValue={demandSortValue}
          pageSize={widget.pageSize}
          paginated={false}
          loading={query.isLoading}
          errorMessage={query.isError ? dashboardT('networkDashboard.loadError') : undefined}
          minWidthClassName="min-w-[720px]"
        />
      </section>
    );
  }
  if (
    widget.type !== NetworkDashboardWidgetType.RevenueDistribution &&
    widget.type !== NetworkDashboardWidgetType.WonBidsDistribution
  ) {
    return null;
  }
  const revenue = widget.type === NetworkDashboardWidgetType.RevenueDistribution;
  const total = revenue ? (query.data?.total.revenue ?? 0) : (query.data?.total.wonBids ?? 0);
  const data = rows.map((row, index) => ({
    id: row.demandPartnerId ?? String(index),
    label: row.demandPartnerName ?? row.demandPartner ?? '-',
    value: total > 0 ? (((revenue ? row.revenue : row.wonBids) ?? 0) / total) * 100 : 0,
  }));
  return (
    <Card className="border-border bg-card h-full min-w-0 rounded-md shadow-[var(--shadow-xs)]">
      <CardHeader className="px-5 pt-5 pb-2">
        <CardTitle className="text-base">
          {dashboardT(
            revenue
              ? 'networkDashboard.revenueDistribution'
              : 'networkDashboard.wonBidsDistribution',
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-5 pb-5">
        {query.isLoading ? <Skeleton className="h-40 w-full" /> : null}
        {!query.isLoading && query.isError ? (
          <div className="text-destructive flex h-40 items-center justify-center text-sm">
            {dashboardT('networkDashboard.loadError')}
          </div>
        ) : null}
        {!query.isLoading && !query.isError && revenue ? (
          <ResponsiveBarChart
            ariaLabel={dashboardT('networkDashboard.revenueDistribution')}
            data={data}
            valueFormatter={(value) => formatPercent(value)}
            emptyMessage={dashboardT('dashboard.widget.empty')}
          />
        ) : null}
        {!query.isLoading && !query.isError && !revenue ? (
          <ResponsiveDonutChart
            ariaLabel={dashboardT('networkDashboard.wonBidsDistribution')}
            data={data}
            valueFormatter={(value) => formatPercent(value)}
            emptyMessage={dashboardT('dashboard.widget.empty')}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

function demandColumns() {
  return buildDashboardDataTableColumns<NetworkDemandSourceRow>([
    {
      id: 'partner',
      header: dashboardT('dashboard.table.partner'),
      kind: DashboardDataTableColumnKind.Text,
      value: (row) => row.demandPartnerName ?? row.demandPartner,
      className: 'w-[220px]',
      sortable: true,
      sortKey: 'partner',
    },
    {
      id: 'bidRequests',
      header: dashboardT('dashboard.table.bidRequests'),
      cell: (row) => formatNumber(row.dpAdRequests),
      sortable: true,
      sortKey: 'bidRequests',
    },
    {
      id: 'rpm',
      header: dashboardT('dashboard.metric.rpm'),
      cell: (row) => formatCurrency(row.rpm),
      sortable: true,
      sortKey: 'rpm',
    },
    {
      id: 'bidFillRatio',
      header: dashboardT('networkDashboard.bidFillRatio'),
      cell: (row) => formatRatio(row.bidFillRatio),
      sortable: true,
      sortKey: 'bidFillRatio',
    },
    {
      id: 'bidRatio',
      header: dashboardT('dashboard.table.bidRatio'),
      cell: (row) => formatRatio(row.bidRatio),
      sortable: true,
      sortKey: 'bidRatio',
    },
  ]);
}

function demandSortValue(row: NetworkDemandSourceRow, key: string) {
  const values: Record<string, () => string | number | undefined> = {
    partner: () => row.demandPartnerName ?? row.demandPartner,
    bidRequests: () => row.dpAdRequests,
    rpm: () => row.rpm,
    bidFillRatio: () => row.bidFillRatio,
    bidRatio: () => row.bidRatio,
  };
  return values[key]?.();
}
